import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import type { CursorPage, EmployeeDetail as Employee, Plant } from '@dru/types';
import { Button, ErrorState, TextSkeleton } from '@dru/ui';
import { ArrowLeft, Printer } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { EmployeeAvatar, useEmployeePhoto } from '../components/EmployeePhoto';
import { BRAND } from '@dru/types';

/**
 * The ID slip: company, name, code and plant, the photo, and
 * the employee code as a QR.
 *
 * The QR carries the EMPLOYEE CODE and nothing else. That is what the
 * check-in desk verifies off the ID card when a worker has no pass
 * ("Type the code from the employee's ID card"), and a wedge scanner reading
 * this slip into that field is the same act with less typing. The pass QR
 * the desk scans first is a different thing on purpose: a one-time secret
 * issued per booking, shown once and never re-fetchable — a card that is
 * photocopied, lost or borrowed must not be able to check someone in.
 *
 * Two slips per A4: one for the worker, one for the file. Opened in its own
 * tab from the record, outside the console shell, so what prints is the
 * slips and only the slips.
 */

const QR_SIZE_PX = 480;

export function EmployeePrintQr() {
  const { id = '' } = useParams();
  const { principal } = useAuth();
  const company = principal?.tenant?.name ?? '';

  // Not asked at all without a company: the API would only fail.
  const inCompany = Boolean(principal?.tenant);
  const employee = useQuery<Employee>(
    () => (inCompany ? api.get<Employee>(`/employees/${id}`) : new Promise<Employee>(() => {})), [id, inCompany]);
  const plants = useQuery<CursorPage<Plant>>(
    () => (inCompany ? api.get<CursorPage<Plant>>('/plants?limit=100') : new Promise<CursorPage<Plant>>(() => {})), [inCompany]);
  const photo = useEmployeePhoto(employee.data?.id, employee.data?.hasPhoto, employee.data?.photoUpdatedAt);

  const e = employee.data;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!e) return;
    let live = true;
    QRCode.toDataURL(e.employeeCode, { errorCorrectionLevel: 'M', margin: 1, width: QR_SIZE_PX })
      .then((url) => { if (live) setQr(url); })
      .catch(() => { if (live) setQr(null); });
    return () => { live = false; };
  }, [e]);

  useEffect(() => { if (e) document.title = `ID slip — ${e.fullName} (${e.employeeCode})`; }, [e]);

  // Print once, when everything that goes on the slip is on the page —
  // including the photo, if there is one and it has not failed to arrive.
  const photoSettled = !e?.hasPhoto || photo !== null;
  // The logo is on the card too: printing before it arrives prints a gap.
  const [logoSettled, setLogoSettled] = useState(false);
  const ready = Boolean(e && qr && plants.data && photoSettled && logoSettled);
  const printed = useRef(false);
  useEffect(() => {
    if (!ready || printed.current) return;
    printed.current = true;
    const t = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(t);
  }, [ready]);

  // A login with no company (a platform administrator, or a new tab that
  // lost an impersonation session) cannot read company employees at all.
  if (!inCompany) {
    return (
      <main className="mx-auto max-w-xl p-4">
        <ErrorState
          title="Open the ID card from the company's console"
          detail="This login is not signed in to a company. If you were viewing as a company user, go back to that tab and use Print QR card there."
        />
      </main>
    );
  }

  if (employee.error) {
    return (
      <main className="mx-auto max-w-xl p-4">
        <ErrorState title="This employee could not be opened" detail={employee.error} onRetry={employee.reload} />
      </main>
    );
  }

  const plantName = plants.data?.items.find((p) => p.id === e?.plantId)?.name ?? null;

  return (
    <main className="print-qr min-h-screen bg-neutral-100 text-neutral-900">
      <style>{PRINT_CSS}</style>

      <div className="no-print mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-3">
        <Link to={`/hr/employees/${id}`} className="inline-flex min-h-10 items-center gap-1.5 text-small font-medium text-primary-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to record
        </Link>
        <Button icon={<Printer className="h-4 w-4" />} onClick={() => window.print()} disabled={!ready}>
          Print
        </Button>
      </div>

      <div className="sheet mx-auto bg-white">
        {!e ? (
          <div className="p-6"><TextSkeleton lines={3} /></div>
        ) : (
          <>
            <Slip e={e} company={company} plant={plantName} photo={photo} qr={qr} who="Employee copy"
                  onLogo={() => setLogoSettled(true)} />
            <div className="cut" aria-hidden><span>cut here</span></div>
            <Slip e={e} company={company} plant={plantName} photo={photo} qr={qr} who="File copy" />
          </>
        )}
      </div>

      <p className="no-print mx-auto max-w-3xl px-4 py-4 text-caption text-neutral-500">
        Two slips on one A4 sheet. The QR holds the employee code, which the check-in desk
        verifies off the ID card; it is not a check-in pass.
      </p>
    </main>
  );
}

function Slip({ e, company, plant, photo, qr, who, onLogo }: {
  e: Employee; company: string; plant: string | null;
  photo: string | null; qr: string | null; who: string;
  /** Told once the logo has loaded or failed, so printing can wait for it. */
  onLogo?: () => void;
}) {
  return (
    <section className="slip" aria-label={`ID slip, ${who}`}>
      <header className="slip-head">
        <img className="slip-logo" src={BRAND.logo.full} alt={BRAND.logo.alt} onLoad={onLogo} onError={onLogo} />
        <span className="slip-company" title={company}>{company || BRAND.name}</span>
      </header>
      <div className="slip-band" aria-hidden><span /></div>
      <div className="slip-body">
        <div className="slip-photo">
          <EmployeeAvatar name={e.fullName} src={photo} size="xl" className="slip-avatar" />
        </div>
        <div className="slip-text">
          <p className="slip-name">{e.fullName}</p>
          <p className="slip-label">Employee ID</p>
          <p className="slip-code">{e.employeeCode}</p>
          {plant && <p className="slip-where">{plant}</p>}
          {e.bloodGroup && <p className="slip-blood">Blood group <b>{e.bloodGroup}</b></p>}
        </div>
        <div className="slip-qr">
          {qr
            ? <img src={qr} alt={`QR code of employee code ${e.employeeCode}`} />
            : <span className="slip-qr-wait" />}
          <span className="slip-qr-cap">Scan for ID</span>
        </div>
      </div>
      <footer className="slip-foot">
        <span>Show this card at the medical check-in desk</span>
        <span className="slip-copy">{who}</span>
      </footer>
    </section>
  );
}

/**
 * Millimetre layout, so the slip is the same size on paper as on screen.
 * The sheet is A4 less margins; each slip is a stretched ID card
 * (90 × 56 mm) so the QR scans from a few centimetres away. Brand as on the
 * lab reports: navy structure, a magenta accent, the logo on white.
 */
const PRINT_CSS = `
.print-qr .sheet {
  width: 180mm; max-width: 100%; padding: 10mm;
  display: flex; flex-direction: column; align-items: center; gap: 7mm;
}
.print-qr .slip {
  width: 90mm; max-width: 100%; height: 56mm; box-sizing: border-box;
  border: 0.3mm solid #c3cad6; border-radius: 3mm; overflow: hidden;
  display: flex; flex-direction: column; background: #fff; color: #141a24;
  font-family: Inter, system-ui, sans-serif;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
}
.print-qr .slip-head {
  display: flex; align-items: center; justify-content: space-between; gap: 3mm;
  padding: 2mm 3.5mm 1.6mm; min-height: 0;
}
.print-qr .slip-logo { height: 6.2mm; width: auto; display: block; flex: 0 0 auto; }
.print-qr .slip-company {
  min-width: 0; text-align: right; font-size: 7.6pt; font-weight: 700; color: #1d3461; line-height: 1.15;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.print-qr .slip-band { position: relative; height: 1.6mm; background: #1d3461; flex: 0 0 auto; }
.print-qr .slip-band span {
  position: absolute; left: 30mm; top: 0; height: 100%; width: 9mm;
  background: repeating-linear-gradient(115deg, #fff 0 0.6mm, #d0256c 0.6mm 1.8mm);
}
.print-qr .slip-body { display: flex; align-items: center; gap: 2.8mm; padding: 2.2mm 3.5mm 1.6mm; flex: 1; min-height: 0; }
.print-qr .slip-photo { flex: 0 0 auto; }
.print-qr .slip-avatar { width: 19mm !important; height: 23mm !important; border-radius: 1.6mm !important; font-size: 15pt !important; }
.print-qr .slip-text { flex: 1 1 auto; min-width: 0; }
.print-qr .slip-name {
  font-size: 10pt; font-weight: 700; line-height: 1.15; color: #141a24; overflow-wrap: anywhere;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.print-qr .slip-label { font-size: 5.6pt; letter-spacing: 0.06em; text-transform: uppercase; color: #5a6372; margin-top: 1.3mm; }
.print-qr .slip-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 10.5pt; font-weight: 700; color: #1d3461; letter-spacing: 0.02em; }
.print-qr .slip-where {
  font-size: 7pt; color: #374151; margin-top: 0.9mm; line-height: 1.2; overflow-wrap: anywhere;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.print-qr .slip-blood { font-size: 7pt; color: #374151; margin-top: 0.5mm; }
.print-qr .slip-blood b { color: #b3122e; }
.print-qr .slip-qr { flex: 0 0 auto; width: 23mm; display: flex; flex-direction: column; align-items: center; gap: 0.6mm; }
.print-qr .slip-qr img { width: 23mm; height: 23mm; display: block; image-rendering: pixelated; }
.print-qr .slip-qr-wait { display: block; width: 23mm; height: 23mm; background: #f3f4f6; }
.print-qr .slip-qr-cap { font-size: 5.6pt; letter-spacing: 0.06em; text-transform: uppercase; color: #5a6372; }
.print-qr .slip-foot {
  display: flex; justify-content: space-between; align-items: center; gap: 2mm;
  background: #1d3461; color: #fff; font-size: 6.2pt; padding: 1.2mm 3.5mm; line-height: 1.2;
}
.print-qr .slip-copy { opacity: 0.75; white-space: nowrap; }
.print-qr .cut { width: 100%; max-width: 120mm; border-top: 0.2mm dashed #9ca3af; position: relative; text-align: center; }
.print-qr .cut span { position: relative; top: -2mm; background: #fff; padding: 0 2mm; font-size: 6pt; color: #9ca3af; letter-spacing: 0.06em; text-transform: uppercase; }
@media screen and (max-width: 420px) {
  .print-qr .sheet { padding: 4mm; }
}
@page { size: A4 portrait; margin: 12mm; }
@media print {
  html, body { background: #fff !important; }
  .print-qr { background: #fff !important; min-height: 0 !important; }
  .print-qr .no-print { display: none !important; }
  .print-qr .sheet { width: auto; padding: 0; gap: 9mm; }
  .print-qr .slip { break-inside: avoid; }
}
`;
